import * as stylex from "@stylexjs/stylex";
import { CalendarDays, Check, Image, type LucideIcon, MonitorSmartphone, StickyNote, Trash2, TriangleAlert, Users } from "lucide-react";
import { useId } from "react";
import { copy } from "@/copy";
import type { AccountCounts } from "@/data/repos";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import { Banner } from "@/ui/Banner";
import { Button } from "@/ui/Button";
import { Dialog } from "@/ui/Dialog";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import type { DeleteAccountDialog as DeleteAccountState } from "../deleteAccountDialog";
type DeleteAccountDialogProps = {
  email: string;
  counts: AccountCounts | undefined;
  screens: number;
  state: Exclude<DeleteAccountState, {
    kind: "closed";
  }>;
  onToggle: () => void;
  onConfirm: () => void;
  onCancel: () => void;
};
type ErasedItem = {
  icon: LucideIcon;
  label: string;
  count: number | undefined;
};
const text = copy.settings.account.deleteDialog;
const styles = {
  count: {
    kmuXW: "x2lah0s",
    kcqcaj: "xss6m8b",
    $$css: true
  }
};
export function DeleteAccountDialog({
  email,
  counts,
  screens,
  state,
  onToggle,
  onConfirm,
  onCancel
}: DeleteAccountDialogProps) {
  const titleId = useId();
  const busy = state.kind === "deleting";
  const checked = state.kind !== "ask";
  return <Dialog labelledBy={titleId} alert busy={busy} onClose={onCancel}>
      <span className="xrvj5dj x1ku5rj1 x1fu8urw x1peatla x2lah0s xqhrb7l xi9wlog xphs7x8">
        <Icon icon={Trash2} size={28} strokeWidth={1.5} />
      </span>
      <div className="x78zum5 xdt5ytf x1v2ro7d">
        <Eyebrow>{email}</Eyebrow>
        <h2 id={titleId} className="x1ghz6dp xneq65o x1v94iar xrg9stw x1159mfc">
          {text.title}
        </h2>
        <p className="x1ghz6dp x1j61zf2 x15i68ds xfrs9s4 x1fzhlzt">{text.body}</p>
      </div>
      <ul aria-label={text.erased} className="x78zum5 xdt5ytf x1ghz6dp xt970qd x1hr3lfm xe8uvvx x5r8ks9 x1fv5t7x">
        {erasedItems(counts, screens).map((item, index) => <li key={item.label} {...{
        0: {
          className: "x78zum5 x6s0dn4 x1af02g3 x1wiwyrm"
        },
        1: {
          className: "x78zum5 x6s0dn4 x1af02g3 x1wiwyrm x1jidhvw"
        }
      }[!!(index > 0) << 0]}>
            <span className="x78zum5 x2lah0s">
              <Icon icon={item.icon} size={20} />
            </span>
            <span className="x1iyjqo2 xeuugli x19d36u7 x15i68ds">{item.label}</span>
            <Eyebrow size="caption" xstyle={styles.count}>
              {item.count === undefined ? text.loading : String(item.count)}
            </Eyebrow>
          </li>)}
      </ul>
      {busy ? <div role="status" className="x78zum5 x6s0dn4 xbktkl8 x19d36u7 x1v94iar x1xfvgam x1fzhlzt">
          {text.erasing}
        </div> :
    // biome-ignore lint/a11y/useSemanticElements: a styled button keeps the 56 px row, the 28 px box and the press scale; role and state follow the ARIA checkbox
    <button type="button" role="checkbox" aria-checked={checked} onClick={onToggle} className="x78zum5 x6s0dn4 x1af02g3 xbktkl8 x1717udv xc342km xjbqb8w xjb2p0i x1a11ngh xdpxx8g x1ypdohk x11xpdln x1bpocu2 x3m7wy4">
          <span {...{
        0: {
          className: "xrvj5dj x1ku5rj1 xgd8bvy x1fgtraw x2lah0s x1fv5t7x xh540kj x1pl751m xnnvkky"
        },
        1: {
          className: "xrvj5dj x1ku5rj1 xgd8bvy x1fgtraw x2lah0s x1fv5t7x xnnvkky xfenp3h x1gnnqk1"
        }
      }[!!checked << 0]}>
            {checked && <Icon icon={Check} size={18} strokeWidth={2.5} />}
          </span>
          <span className="x19d36u7 x1v94iar x1xfvgam x1fzhlzt">{text.understand}</span>
        </button>}
      {state.kind === "failed" && <div role="alert">
          <Banner tone="soft" icon={TriangleAlert} title={text.failedTitle} body={text.failedBody} actionLabel={text.retry} onAction={onConfirm} />
        </div>}
      <div className="x78zum5 xdt5ytf x1v2ro7d">
        <Button variant="danger" icon={Trash2} fullWidth disabled={!checked || busy} onClick={onConfirm}>
          {busy ? text.deleting : text.confirm}
        </Button>
        <Button variant="ghost" fullWidth disabled={busy} onClick={onCancel}>
          {text.keep}
        </Button>
      </div>
    </Dialog>;
}
function erasedItems(counts: AccountCounts | undefined, screens: number): ErasedItem[] {
  return [{
    icon: CalendarDays,
    label: text.events,
    count: counts?.events
  }, {
    icon: StickyNote,
    label: text.notes,
    count: counts?.notes
  }, {
    icon: Image,
    label: text.photos,
    count: counts?.photos
  }, {
    icon: Users,
    label: text.tags,
    count: counts?.tags
  }, {
    icon: MonitorSmartphone,
    label: text.screens,
    count: screens
  }];
}